import { Paper, Theme, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveSuggestions } from '../../redux/features/aiSuggestions/aiSuggestions.selector';
import { dismissSuggestion } from '../../redux/features/aiSuggestions/aiSuggestions.slice';
import { IAISuggestion } from '../../redux/features/aiSuggestions/aiSuggestions.props';
import useSendMessageHooks from '../../hooks/useSendMessage/useSendMessage.hooks';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import { AgentInterceptionType } from '../../reducers/messagingSlice';
import logger from '../../utils/logger';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1, 1, 1),
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    border: '1px solid #c5cae9',
    backgroundColor: '#eef1fb',
    borderRadius: '8px',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  title: {
    fontWeight: 'bold !important' as 'bold',
    fontSize: '0.9rem',
  },
  reason: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
}));

/**
 * Renders incoming AI suggestions (the "advise" side of the co-pilot) at the
 * bottom of the left pane. The human agent stays the driver: a suggestion is
 * never applied automatically — only on an explicit Accept.
 *
 * TODO (driver-rule gate): render only post-interception (isAIAutoMode === false).
 * Left ungated for now so the wire is easy to verify; wire the gate with the
 * apply step.
 */
const AISuggestions = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const suggestions = useSelector(selectActiveSuggestions);
  const { sendMessage } = useSendMessageHooks();
  const { sendAgentInterception } = useAgentInterception();

  if (!suggestions.length) {
    return null;
  }

  const handleAccept = (suggestion: IAISuggestion) => {
    if (suggestion.tts) {
      // Any suggestion that carries a `tts` line is speakable, regardless of its
      // suggestion_type. Speak it via the normal HITL send path (status=final
      // -> TTS), and mark the agent as having taken the turn (TTS_SENT), exactly
      // like an intent pill / OmniBar send.
      sendAgentInterception({
        data: { type: AgentInterceptionType.TTS_SENT },
      });
      sendMessage(suggestion.tts, { intent: 'ai_suggestion' });
      logger.log({
        message: `AI suggestion accepted (tts): "${suggestion.tts}" (${suggestion.suggestion_id})`,
      });
    } else {
      // TODO (step 2): map suggestion.action -> a cartSlice mutation
      // (selectModifier / addItemToCart) for cart-action suggestions.
      logger.log({
        message: `AI suggestion accepted: ${suggestion.suggestion_type} (${suggestion.suggestion_id})`,
        moreInfo: JSON.stringify(suggestion.action),
      });
    }
    dispatch(
      dismissSuggestion({
        suggestionType: suggestion.suggestion_type,
        suggestionId: suggestion.suggestion_id,
      })
    );
  };

  const handleDismiss = (suggestion: IAISuggestion) => {
    logger.log({
      message: `AI suggestion dismissed: ${suggestion.suggestion_type} (${suggestion.suggestion_id})`,
    });
    dispatch(
      dismissSuggestion({
        suggestionType: suggestion.suggestion_type,
        suggestionId: suggestion.suggestion_id,
      })
    );
  };

  return (
    <div className={classes.container} data-testid="ai-suggestions">
      {suggestions.map((suggestion) => (
        <Paper
          key={suggestion.suggestion_id}
          className={classes.card}
          elevation={0}
        >
          <div className={classes.text}>
            <Typography className={classes.title}>
              {suggestion.title}
            </Typography>
            {suggestion.reason && (
              <Typography className={classes.reason}>
                {suggestion.reason}
              </Typography>
            )}
          </div>
          <div className={classes.actions}>
            <IconButton
              size="small"
              color="success"
              title="Accept"
              aria-label="Accept suggestion"
              onClick={() => handleAccept(suggestion)}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Dismiss"
              aria-label="Dismiss suggestion"
              onClick={() => handleDismiss(suggestion)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </Paper>
      ))}
    </div>
  );
};

export default AISuggestions;
